import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Grid,
} from "@mui/material";
import { Save as SaveIcon, Person as PersonIcon } from "@mui/icons-material";
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
// import {
//   getUserProfile,
//   saveUserProfile,
//   checkBsaNumberAvailability,
// } from "../services/userProfile";

const schema = yup.object().shape({
  firstName: yup
    .string()
    .required("First name is required")
    .min(2, "First name must be at least 2 characters"),
  lastName: yup
    .string()
    .required("Last name is required")
    .min(2, "Last name must be at least 2 characters"),
  bsaNumber: yup
    .string()
    .required("BSA number is required")
    .matches(/^[0-9]+$/, "BSA number must contain only numbers"),
  phoneNumber: yup
    .string()
    .required("Phone number is required")
    .matches(/^[\+]?[1-9][\d]{0,15}$/, "Please enter a valid phone number"),
});

function Profile() {
  const { user, loading: userLoading } = useKindeAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [profile, setProfile] = useState(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      firstName: "",
      lastName: "",
      bsaNumber: "",
      phoneNumber: "",
    },
  });

  const watchedBsaNumber = watch("bsaNumber");

  // Load user profile on component mount
  useEffect(() => {
    const loadProfile = async () => {
      if (!user?.id) return;

      try {
        // const response = await getUserProfile(user.id);
        // if (response.success && response.data) {
        //   setProfile(response.data);
        //   reset({
        //     firstName: response.data.first_name || "",
        //     lastName: response.data.last_name || "",
        //     bsaNumber: response.data.bsa_number || "",
        //     phoneNumber: response.data.phone_number || "",
        //   });
        // } else {
        //   // Try to get data from auth metadata if no profile exists
        //   reset({
        //     firstName: user.user_metadata?.first_name || "",
        //     lastName: user.user_metadata?.last_name || "",
        //     bsaNumber: user.user_metadata?.bsa_number || "",
        //     phoneNumber: user.user_metadata?.phone_number || "",
        //   });
        // }
      } catch (error) {
        console.error("Error loading profile:", error);
        setMessage({ type: "error", text: "Failed to load profile data" });
      } finally {
        setLoading(false);
      }
    };

    if (!userLoading && user) {
      loadProfile();
    }
  }, [user, userLoading, reset]);

  const onSubmit = async (data) => {
    setSaving(true);
    setMessage(null);

    try {
      // Check if BSA number is available (if changed)
      if (data.bsaNumber !== profile?.bsa_number) {
        // const availabilityCheck = await checkBsaNumberAvailability(
        //   data.bsaNumber,
        //   user.id
        // );
        // if (!availabilityCheck.success) {
        //   throw new Error("Failed to check BSA number availability");
        // }
        // if (!availabilityCheck.available) {
        //   setMessage({
        //     type: "error",
        //     text: "This BSA number is already in use by another user",
        //   });
        //   setSaving(false);
        //   return;
        // }
      }

      // const response = await saveUserProfile(user.id, data);

      // if (response.success) {
      //   setProfile(response.data);
      //   setMessage({
      //     type: "success",
      //     text: "Profile updated successfully!",
      //   });
      // } else {
      //   throw new Error(response.error?.message || "Failed to save profile");
      // }
    } catch (error) {
      console.error("Error saving profile:", error);
      setMessage({
        type: "error",
        text: error.message || "Failed to save profile",
      });
    } finally {
      setSaving(false);
    }
  };

  if (userLoading || loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return (
      <Alert severity="error">
        You must be logged in to view your profile.
      </Alert>
    );
  }

  return (
    <Box sx={{ maxWidth: 600, mx: "auto", mt: 4 }}>
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" mb={3}>
            <PersonIcon sx={{ mr: 2, fontSize: 32, color: "primary.main" }} />
            <Typography variant="h4" component="h1">
              My Profile
            </Typography>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {message && (
            <Alert
              severity={message.type}
              sx={{ mb: 3 }}
              onClose={() => setMessage(null)}
            >
              {message.text}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  {...register("firstName")}
                  label="First Name"
                  fullWidth
                  error={!!errors.firstName}
                  helperText={errors.firstName?.message}
                  disabled={saving}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  {...register("lastName")}
                  label="Last Name"
                  fullWidth
                  error={!!errors.lastName}
                  helperText={errors.lastName?.message}
                  disabled={saving}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  value={user.email}
                  label="Email"
                  fullWidth
                  disabled
                  helperText="Email cannot be changed"
                  sx={{
                    "& .MuiInputBase-input.Mui-disabled": {
                      WebkitTextFillColor: "rgba(0, 0, 0, 0.6)",
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  {...register("bsaNumber")}
                  label="BSA Number"
                  fullWidth
                  error={!!errors.bsaNumber}
                  helperText={
                    errors.bsaNumber?.message ||
                    "Your unique BSA (Bowls South Africa) number"
                  }
                  disabled={saving}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  {...register("phoneNumber")}
                  label="Phone Number"
                  fullWidth
                  error={!!errors.phoneNumber}
                  helperText={
                    errors.phoneNumber?.message ||
                    "Include country code (e.g., +27 for South Africa)"
                  }
                  disabled={saving}
                />
              </Grid>

              <Grid item xs={12}>
                <Box display="flex" justifyContent="flex-end" mt={2}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    startIcon={
                      saving ? <CircularProgress size={20} /> : <SaveIcon />
                    }
                    disabled={saving}
                  >
                    {saving ? "Saving..." : "Save Profile"}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {profile && (
            <Box sx={{ mt: 4, pt: 3, borderTop: 1, borderColor: "divider" }}>
              <Typography variant="body2" color="text.secondary">
                Profile last updated:{" "}
                {new Date(profile.updated_at).toLocaleString()}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

export default Profile;
